import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, Layers, Sparkles, Trello, PartyPopper,
  ChevronLeft, ChevronRight, X, ArrowRight, Check,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";

export const TOUR_STORAGE_KEY = "alp.tour.completed.v1";

const STEPS = [
  {
    id: "welcome",
    icon: PartyPopper,
    color: "#3B82F6",
    titleKey: "tour.s1.title",
    bodyKey: "tour.s1.body",
    bulletsKeys: ["tour.s1.b1", "tour.s1.b2", "tour.s1.b3"],
  },
  {
    id: "prospect",
    icon: Search,
    color: "#3B82F6",
    titleKey: "tour.s2.title",
    bodyKey: "tour.s2.body",
    bulletsKeys: ["tour.s2.b1", "tour.s2.b2", "tour.s2.b3"],
    cta: { labelKey: "tour.s2.cta", to: "/" },
  },
  {
    id: "score",
    icon: Sparkles,
    color: "#A855F7",
    titleKey: "tour.s3.title",
    bodyKey: "tour.s3.body",
    bulletsKeys: ["tour.s3.b1", "tour.s3.b2", "tour.s3.b3"],
  },
  {
    id: "kanban",
    icon: Trello,
    color: "#16A34A",
    titleKey: "tour.s4.title",
    bodyKey: "tour.s4.body",
    bulletsKeys: ["tour.s4.b1", "tour.s4.b2", "tour.s4.b3"],
    cta: { labelKey: "tour.s4.cta", to: "/kanban" },
  },
  {
    id: "ready",
    icon: Layers,
    color: "#F59E0B",
    titleKey: "tour.s5.title",
    bodyKey: "tour.s5.body",
    bulletsKeys: ["tour.s5.b1", "tour.s5.b2", "tour.s5.b3"],
    cta: { labelKey: "tour.s5.cta", to: "/dashboard" },
  },
];

export default function OnboardingTour({ open, onClose }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);

  useEffect(() => { if (open) setIdx(0); }, [open]);

  if (!open) return null;
  const step = STEPS[idx];
  const Icon = step.icon;
  const isLast = idx === STEPS.length - 1;
  const isFirst = idx === 0;

  const finish = () => {
    try { window.localStorage.setItem(TOUR_STORAGE_KEY, "1"); } catch (_e) { /* ignore */ }
    onClose?.();
  };

  const next = () => { if (isLast) finish(); else setIdx((i) => i + 1); };
  const back = () => { if (!isFirst) setIdx((i) => i - 1); };

  const onCtaClick = () => {
    finish();
    if (step.cta?.to) navigate(step.cta.to);
  };

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/65 backdrop-blur-sm p-3 sm:p-4"
      onClick={finish}
    >
      <div
        data-testid="onboarding-tour-card"
        className="w-full max-w-[560px] max-h-[92vh] overflow-y-auto al-card p-0 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent bar */}
        <div className="h-1 w-full" style={{ background: step.color }} />

        <div className="p-4 sm:p-7 relative">
          <button
            type="button"
            data-testid="tour-skip"
            onClick={finish}
            title={t("tour.skip")}
            className="absolute top-3 right-3 text-[var(--al-text-muted)] hover:text-[var(--al-text)]"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3 mb-4 pr-6">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `${step.color}1A`, color: step.color }}
            >
              <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] sm:text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold">
                {t("tour.step_label", { current: idx + 1, total: STEPS.length })}
              </div>
              <h2 className="text-[16px] sm:text-[18px] font-bold text-[var(--al-text)] leading-tight">
                {t(step.titleKey)}
              </h2>
            </div>
          </div>

          <p className="text-[12.5px] sm:text-[13.5px] text-[var(--al-text-muted)] leading-relaxed">
            {t(step.bodyKey)}
          </p>

          <ul className="mt-3 sm:mt-4 space-y-1.5 sm:space-y-2">
            {step.bulletsKeys.map((bk, i) => (
              <li key={bk} data-testid={`tour-bullet-${idx}-${i}`} className="flex items-start gap-2 sm:gap-2.5 text-[12px] sm:text-[12.5px] text-[var(--al-text)]">
                <span
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: `${step.color}1A`, color: step.color }}
                >
                  <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3" strokeWidth={3} />
                </span>
                <span className="leading-snug">{t(bk)}</span>
              </li>
            ))}
          </ul>

          {step.cta && (
            <div className="mt-4 sm:mt-5">
              <Link
                to={step.cta.to}
                data-testid={`tour-cta-${step.id}`}
                onClick={onCtaClick}
                className="inline-flex items-center gap-1.5 px-3.5 sm:px-4 h-8 sm:h-9 rounded-lg text-[12px] sm:text-[12.5px] font-semibold transition-colors"
                style={{
                  background: `${step.color}15`,
                  color: step.color,
                  border: `1px solid ${step.color}50`,
                }}
              >
                {t(step.cta.labelKey)} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>

        {/* Footer: dots + nav */}
        <div className="px-4 sm:px-7 py-3 sm:py-4 border-t border-[var(--al-border)] flex items-center justify-between gap-3 bg-[var(--al-bg)] sticky bottom-0">
          <div className="flex items-center gap-1.5" data-testid="tour-progress">
            {STEPS.map((s, i) => (
              <span
                key={s.id}
                data-testid={`tour-dot-${i}${i === idx ? "-active" : ""}`}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === idx ? 22 : 8,
                  background: i === idx ? step.color : "var(--al-border)",
                }}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="tour-back"
              onClick={back}
              disabled={isFirst}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[var(--al-border)] text-[12px] font-medium text-[var(--al-text-muted)] hover:text-[var(--al-text)] hover:border-[var(--al-text-muted)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              {t("tour.back")}
            </button>
            <button
              type="button"
              data-testid="tour-next"
              onClick={next}
              className="inline-flex items-center gap-1 h-8 px-3.5 rounded-lg text-[12px] font-semibold text-white"
              style={{ background: step.color }}
            >
              {isLast ? t("tour.done") : t("tour.next")}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
